/* eslint-disable relay/unused-fields */
import { useMemo } from "react";
import type { FunctionComponent, ReactNode } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type {
  liveContentLoaderQuery,
  liveContentLoaderQuery$data,
} from "scmp-app/queries/__generated__/liveContentLoaderQuery.graphql";

import type { EntityIdUuidMapEntry } from "./types";

type Payload = {
  data: liveContentLoaderQuery$data;
  entityIdUuidMap: Record<string, EntityIdUuidMapEntry>;
};

type Props = liveContentLoaderQuery["variables"] & {
  children: (payload: Payload) => ReactNode;
};

const query = graphql`
  query liveContentLoaderQuery($entityUuid: String, $randomString: String) {
    liveArticle(filter: { entityUuid: $entityUuid, randomString: $randomString }) {
      status
      liveContents {
        entityId
        entityUuid
        customTime
        ...summaryLiveArticleSummaryLiveContent
        ...timelineLiveArticleLiveContent
      }
      ...contentLiveArticleLiveArticle
      ...liveUpdateSwitchLiveArticle
    }
  }
`;

export const LiveContentLoader: FunctionComponent<Props> = ({ children, ...variables }) => {
  const queryReference = useLazyLoadQuery<liveContentLoaderQuery>(query, variables, {
    fetchPolicy: "store-and-network",
  });

  const entityIdUuidMap = useMemo(
    () =>
      Object.fromEntries(
        queryReference.liveArticle?.liveContents
          ?.filter(notEmpty)
          ?.filter(
            ({ customTime, entityId, entityUuid }) =>
              notEmpty(customTime) && notEmpty(entityId) && notEmpty(entityUuid),
          )
          ?.map(entry => entry as EntityIdUuidMapEntry)
          ?.map(entry => [entry.entityId, entry]) ?? [],
      ),
    [queryReference.liveArticle?.liveContents],
  );

  return <>{children({ data: queryReference, entityIdUuidMap })}</>;
};

LiveContentLoader.displayName = "LiveContentLoader";
